import { CloudUploadOutlined, DeleteOutlined, HolderOutlined, PictureOutlined } from "@ant-design/icons";
import { onlineManager } from "@tanstack/react-query";
import { Button, Image, Space, Spin, Typography, Upload, message } from "antd";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { useGetOrganization } from "../../../../api/v1";
import { UPLOAD_CONFIG } from "../../../libs/constants";
import { uploadDocumentMedia } from "../../../../utils/media";
import { antdBeforeUploadAllowList } from "../../../../utils/uploadValidation";
import { UploadedDocument } from "../../../../types/document";
import { GalleryFileItem, GallerySourceImage, SaveGalleryPayload } from "../types";
import { reorderList, sortSourceImages, toGalleryImagesPayload, buildUploadPath } from "../utils";

const { Text, Title } = Typography;

const DEFAULT_MAX_IMAGE_MB = 10;

const OFFLINE_UPLOAD_MESSAGE =
  "Connection lost while uploading. Please reconnect and try uploading again.";

const isNetworkUploadError = (error: unknown) => {
  if (!onlineManager.isOnline()) return true;

  if (!axios.isAxiosError(error)) return false;

  return !error.response && (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED");
};

type GalleryUploadPathSegments = {
  propertyName?: string;
  unitCode?: string;
};

type SortableImageGalleryProps = {
  assetId: string;
  images?: GallerySourceImage[];
  maxImages?: number;
  title?: string;
  contentPurpose?: string;
  saveButtonLabel?: string;
  isLoadingImages?: boolean;
  uploadPathSegments?: GalleryUploadPathSegments;
  onSave?: (payload: SaveGalleryPayload) => Promise<void>;
  onSaveSuccess?: () => void;
};

const getBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
  });

const dedupeFiles = (files: File[]) =>
  files.filter(
    (file, index, list) =>
      list.findIndex(
        (candidate) =>
          candidate.name === file.name && candidate.size === file.size && candidate.lastModified === file.lastModified,
      ) === index,
  );

const isDesktopFileDrag = (event: React.DragEvent<HTMLElement>) => {
  const types = Array.from(event.dataTransfer?.types ?? []);
  return types.includes("Files") && !types.some((type) => type.startsWith("application/x-rbd"));
};

const getDesktopDroppedFiles = (event: React.DragEvent<HTMLElement>) => {
  const items = event.dataTransfer?.items;
  if (items?.length) {
    return Array.from(items)
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter((file): file is File => Boolean(file));
  }
  return Array.from(event.dataTransfer?.files ?? []);
};

export const SortableImageGallery = (props: SortableImageGalleryProps) => {
  const {
    assetId,
    images = [],
    maxImages = 6,
    title = "Images",
    saveButtonLabel = "Save Images",
    isLoadingImages = false,
    uploadPathSegments,
    onSave,
    onSaveSuccess,
  } = props;

  const { organization } = useGetOrganization({ staleTimeInfinity: true });

  const maxImageMb = useMemo(() => {
    const configured = Number(organization?.marketingImageSize);
    return Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_MAX_IMAGE_MB;
  }, [organization?.marketingImageSize]);

  const galleryImageUploadConfig = useMemo(
    () => ({
      ...UPLOAD_CONFIG.IMAGE,
      maxBytes: maxImageMb * 1024 * 1024,
    }),
    [maxImageMb],
  );

  const allowedTypesLabel = useMemo(
    () => UPLOAD_CONFIG.IMAGE.allowedExtensions.map((ext) => ext.replace(".", "").toUpperCase()).join(", "),
    [],
  );

  const [fileList, setFileList] = useState<GalleryFileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDesktopDragOver, setIsDesktopDragOver] = useState(false);
  const [isOnline, setIsOnline] = useState(onlineManager.isOnline());

  const fileInputId = useMemo(() => `gallery-file-input-${assetId || "default"}`, [assetId]);

  const hasUploadRoom = fileList.length < maxImages && !isUploading;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileListRef = useRef(fileList);
  const isProcessingBatchRef = useRef(false);
  const desktopDragDepthRef = useRef(0);
  const offlineAlertShownRef = useRef(false);
  const uploadContextRef = useRef({
    organizationName: organization?.name,
    uploadPathSegments,
    galleryImageUploadConfig,
    maxImages,
  });

  uploadContextRef.current = {
    organizationName: organization?.name,
    uploadPathSegments,
    galleryImageUploadConfig,
    maxImages,
  };

  const updateFileList = (updater: GalleryFileItem[] | ((prev: GalleryFileItem[]) => GalleryFileItem[])) => {
    setFileList((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      fileListRef.current = next;
      return next;
    });
  };

  const imagesSignature = useMemo(
    () => images.map((image) => String(image.documentId || image.data || image.object_id || "")).join("|"),
    [images],
  );

  const emptySlots = useMemo(() => Math.max(maxImages - fileList.length, 0), [fileList.length, maxImages]);

  const getAvailableSlots = () => Math.max(uploadContextRef.current.maxImages - fileListRef.current.length, 0);

  const hydrateExistingImages = async (sourceImages: GallerySourceImage[]) => {
    setLoading(true);
    try {
      const sorted = sortSourceImages(sourceImages);
      const nextFiles = await Promise.all(
        sorted.map(async (image, index) => {
          const documentUrl = image.documentUrl || image.url || "";
          return {
            uid: String(image.object_id ?? `${image.documentId || image.data}-${index}`),
            name: image.data || image.documentId || `image-${index + 1}`,
            status: "done" as const,
            url: documentUrl,
            documentUrl,
            objectKey: image.data,
            documentId: image.documentId || image.data,
            isExisting: true,
          };
        }),
      );

      updateFileList(nextFiles.filter((item) => item.url || item.documentUrl || item.documentId || item.objectKey));
      setDirty(false);
    } catch {
      message.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dirty || isProcessingBatchRef.current) {
      return;
    }

    if (!images?.length) {
      updateFileList([]);
      setDirty(false);
      return;
    }

    hydrateExistingImages(images);
  }, [imagesSignature, dirty]);

  useEffect(() => {
    return onlineManager.subscribe(() => {
      const online = onlineManager.isOnline();
      setIsOnline(online);

      if (online && offlineAlertShownRef.current) {
        message.info("Connection restored. You can upload again.");
        offlineAlertShownRef.current = false;
      }
    });
  }, []);

  const showOfflineUploadWarning = () => {
    if (offlineAlertShownRef.current) {
      return;
    }

    offlineAlertShownRef.current = true;
    message.warning(OFFLINE_UPLOAD_MESSAGE, 5);
  };

  const clearPendingUploadPlaceholders = () => {
    updateFileList((prev) => prev.filter((item) => item.status !== "uploading"));
  };

  type PendingUpload = {
    file: File;
    uid: string;
    previewUrl: string;
  };

  const uploadPendingFile = async (pending: PendingUpload) => {
    const { organizationName, uploadPathSegments: pathSegments } = uploadContextRef.current;
    const { file, uid } = pending;

    try {
      const path = buildUploadPath(organizationName, pathSegments);
      const response = await uploadDocumentMedia(path ? { file_asset: file, path } : { file_asset: file }, false);

      const uploadedDocument = response?.data?.result as UploadedDocument;
      if (!uploadedDocument?.documentId) {
        throw new Error("Upload response did not include a documentId");
      }

      const documentUrl = uploadedDocument.documentUrl || "";
      if (!documentUrl) {
        throw new Error("Upload response did not include a documentUrl");
      }

      updateFileList((prev) =>
        prev.map((item) =>
          item.uid === uid
            ? {
                ...item,
                status: "done",
                documentId: uploadedDocument.documentId,
                documentUrl,
                url: documentUrl || item.url,
              }
            : item,
        ),
      );
    } catch (error) {
      if (isNetworkUploadError(error)) {
        showOfflineUploadWarning();
      } else {
        message.error(`Failed to upload ${file.name}`);
      }

      updateFileList((prev) => prev.filter((item) => item.uid !== uid));
    }
  };

  const processSelectedFiles = async (selectedFiles: File[]) => {
    if (isProcessingBatchRef.current) {
      return;
    }

    if (!onlineManager.isOnline()) {
      message.warning("You are offline. Connect to the internet and try again.");
      return;
    }

    const availableSlots = getAvailableSlots();
    const uniqueFiles = dedupeFiles(selectedFiles);

    if (availableSlots <= 0) {
      message.error(`You can upload a maximum of ${maxImages} images`);
      return;
    }

    if (uniqueFiles.length > availableSlots) {
      message.error(
        `You selected ${uniqueFiles.length} images but can only add ${availableSlots}. Maximum is ${maxImages}.`,
      );
      return;
    }

    const filesToUpload = uniqueFiles;
    if (filesToUpload.length === 0) {
      return;
    }

    isProcessingBatchRef.current = true;
    setIsUploading(true);
    setDirty(true);

    const { galleryImageUploadConfig: uploadConfig } = uploadContextRef.current;
    const pendingUploads: PendingUpload[] = [];

    try {
      for (const file of filesToUpload) {
        const validationResult = await antdBeforeUploadAllowList(file, uploadConfig, (msg) => message.error(msg));
        if (validationResult === Upload.LIST_IGNORE) {
          continue;
        }

        const previewUrl = await getBase64(file);
        pendingUploads.push({
          file,
          uid: `${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`,
          previewUrl,
        });
      }

      if (pendingUploads.length === 0) {
        return;
      }

      const uploadsToRun = pendingUploads.slice(
        0,
        Math.max(uploadContextRef.current.maxImages - fileListRef.current.length, 0),
      );

      if (uploadsToRun.length === 0) {
        return;
      }

      const placeholders: GalleryFileItem[] = uploadsToRun.map((pending) => ({
        uid: pending.uid,
        name: pending.file.name,
        status: "uploading" as const,
        url: pending.previewUrl,
        preview: pending.previewUrl,
        originFileObj: pending.file,
        mimeType: pending.file.name.split(".").pop()?.toLowerCase(),
      }));

      updateFileList((prev) => {
        const slotsLeft = Math.max(uploadContextRef.current.maxImages - prev.length, 0);
        if (slotsLeft <= 0) {
          return prev;
        }
        return [...prev, ...placeholders.slice(0, slotsLeft)];
      });

      for (const pending of uploadsToRun) {
        if (!onlineManager.isOnline()) {
          break;
        }

        await uploadPendingFile(pending);

        if (!onlineManager.isOnline()) {
          break;
        }
      }

      if (!onlineManager.isOnline()) {
        clearPendingUploadPlaceholders();
      }
    } finally {
      isProcessingBatchRef.current = false;
      setIsUploading(false);

      if (onlineManager.isOnline()) {
        offlineAlertShownRef.current = false;
      }
    }
  };

  const handleNativeFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const selectedFiles = input.files ? Array.from(input.files) : [];
    input.value = "";

    if (selectedFiles.length === 0) {
      return;
    }

    void processSelectedFiles(selectedFiles);
  };

  const handleDesktopDragEnter = (event: React.DragEvent<HTMLElement>) => {
    if (!isDesktopFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    desktopDragDepthRef.current += 1;
    setIsDesktopDragOver(true);
  };

  const handleDesktopDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!isDesktopFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = hasUploadRoom ? "copy" : "none";
  };

  const handleDesktopDragLeave = (event: React.DragEvent<HTMLElement>) => {
    if (!isDesktopFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    desktopDragDepthRef.current = Math.max(0, desktopDragDepthRef.current - 1);
    if (desktopDragDepthRef.current === 0) {
      setIsDesktopDragOver(false);
    }
  };

  const handleDesktopDrop = (event: React.DragEvent<HTMLElement>) => {
    if (!isDesktopFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    desktopDragDepthRef.current = 0;
    setIsDesktopDragOver(false);

    if (isUploading) {
      message.warning("Please wait for uploads to finish");
      return;
    }

    if (!onlineManager.isOnline()) {
      message.warning("You are offline. Connect to the internet and try again.");
      return;
    }

    if (!hasUploadRoom) {
      message.error(`You can upload a maximum of ${maxImages} images`);
      return;
    }

    const files = getDesktopDroppedFiles(event);
    if (files.length === 0) {
      return;
    }

    void processSelectedFiles(files);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    updateFileList((prev) => reorderList(prev, result.source.index, result.destination!.index));
    setDirty(true);
  };

  const handleRemove = (file: GalleryFileItem) => {
    updateFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!assetId) {
      message.error("Asset id is required to save images");
      return;
    }

    if (!onSave) {
      message.error("Save handler is not configured");
      return;
    }

    if (fileList.some((file) => file.status === "uploading")) {
      message.warning("Please wait for uploads to finish");
      return;
    }

    if (fileList.length > maxImages) {
      message.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    if (fileList.some((file) => !file.documentId || !(file.documentUrl || file.url))) {
      message.error("Every image must have a document id and url before saving");
      return;
    }

    setSaving(true);
    try {
      const pathPrefix = buildUploadPath(organization?.name, uploadPathSegments);
      await onSave({
        assetId,
        galleryImages: toGalleryImagesPayload(fileList, pathPrefix || undefined),
      });
      message.success("Images saved successfully");
      setDirty(false);
      onSaveSuccess?.();
    } catch {
      message.error("Failed to save images");
    } finally {
      setSaving(false);
    }
  };

  const uploadTileStyle: React.CSSProperties = {
    width: 120,
    height: 120,
    border: "1px dashed #bfbfbf",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#fafafa",
    margin: 0,
  };

  const emptySlotStyle: React.CSSProperties = {
    width: 120,
    height: 120,
    borderRadius: 8,
    border: "1px dashed #d9d9d9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#bfbfbf",
    background: "#fcfcfc",
    margin: 0,
  };

  return (
    <Spin spinning={loading || saving || isLoadingImages}>
      <Space direction="vertical" size={16} className="w-100">
        <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
          <Space direction="vertical" size={4}>
            <Title level={5} className="ag-primary mb-0">
              {title}
            </Title>
            <Text type="secondary">
              <HolderOutlined style={{ marginRight: 6 }} />
              Drag images to reorder
            </Text>
            <Text type="secondary" className="smallFont12">
              {allowedTypesLabel} up to {maxImageMb}MB · drop from desktop to upload
            </Text>
            {!isOnline && (
              <Text type="warning" className="smallFont12">
                You are offline. Uploads will fail until your connection is restored.
              </Text>
            )}
          </Space>
          <Space>
            <Text type="secondary">
              {fileList.length} of {maxImages} images
            </Text>
            {dirty && (
              <Button type="primary" loading={saving} onClick={handleSave}>
                {saveButtonLabel}
              </Button>
            )}
          </Space>
        </Space>

        <input
          id={fileInputId}
          ref={fileInputRef}
          type="file"
          accept={UPLOAD_CONFIG.IMAGE.accept}
          multiple
          style={{ display: "none" }}
          onChange={handleNativeFileChange}
        />

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="images-gallery" direction="horizontal">
            {(provided, snapshot) => (
              <div
                onDragEnter={handleDesktopDragEnter}
                onDragOver={handleDesktopDragOver}
                onDragLeave={handleDesktopDragLeave}
                onDrop={handleDesktopDrop}
                style={{
                  outline: "1px dashed transparent",
                  outlineColor: snapshot.isDraggingOver || isDesktopDragOver ? "#d2d2d2" : "transparent",
                  outlineOffset: 4,
                  borderRadius: 8,
                  boxShadow: "none",
                  // background: isDesktopDragOver ? "rgba(22, 119, 255, 0.04)" : undefined,
                }}
              >
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  {hasUploadRoom ? (
                    <label
                      htmlFor={fileInputId}
                      style={{
                        ...uploadTileStyle,
                        cursor: "pointer",
                        opacity: 1,
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <CloudUploadOutlined style={{ fontSize: 22, marginBottom: 8 }} />
                      <Text>Upload</Text>
                      <Text type="secondary" className="smallFont12">
                        Max {maxImages} · {maxImageMb}MB
                      </Text>
                    </label>
                  ) : (
                    <div
                      role="button"
                      tabIndex={-1}
                      aria-disabled
                      style={{
                        ...uploadTileStyle,
                        cursor: "not-allowed",
                        opacity: 0.6,
                      }}
                      onClick={() => {
                        if (getAvailableSlots() <= 0) {
                          message.error(`You can upload a maximum of ${maxImages} images`);
                        }
                      }}
                    >
                      <CloudUploadOutlined style={{ fontSize: 22, marginBottom: 8 }} />
                      <Text>Upload</Text>
                      <Text type="secondary" className="smallFont12">
                        Max {maxImages} · {maxImageMb}MB
                      </Text>
                    </div>
                  )}

                  <Image.PreviewGroup>
                    {fileList.map((file, index) => (
                      <Draggable
                        key={file.uid}
                        draggableId={file.uid}
                        index={index}
                        isDragDisabled={file.status === "uploading"}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            style={{
                              width: 120,
                              height: 120,
                              borderRadius: 8,
                              overflow: "hidden",
                              position: "relative",
                              border: snapshot.isDragging ? "1px solid #1677ff" : "1px solid #e5e7eb",
                              background: "#fff",
                              cursor:
                                file.status === "uploading" ? "default" : snapshot.isDragging ? "grabbing" : "grab",
                              ...dragProvided.draggableProps.style,
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: 6,
                                left: 6,
                                zIndex: 2,
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                background: "#0f172a",
                                color: "#fff",
                                fontSize: 12,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                pointerEvents: "none",
                              }}
                            >
                              {index + 1}
                            </div>
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(file);
                              }}
                              style={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                zIndex: 2,
                                background: "rgba(255,255,255,0.85)",
                                cursor: "pointer",
                              }}
                            />
                            <Image
                              src={file.url || file.preview}
                              alt={file.name}
                              width={120}
                              height={120}
                              style={{ objectFit: "cover" }}
                              draggable={false}
                              preview={file.status === "uploading" ? false : undefined}
                            />
                            {file.status === "uploading" && (
                              <div
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  background: "rgba(255,255,255,0.55)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Spin size="small" />
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </Image.PreviewGroup>

                  {Array.from({ length: emptySlots }).map((_, index) =>
                    hasUploadRoom ? (
                      <label
                        key={`empty-${index}`}
                        htmlFor={fileInputId}
                        style={{
                          ...emptySlotStyle,
                          cursor: "pointer",
                        }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <PictureOutlined style={{ fontSize: 28 }} />
                      </label>
                    ) : (
                      <div key={`empty-${index}`} style={emptySlotStyle}>
                        <PictureOutlined style={{ fontSize: 28 }} />
                      </div>
                    ),
                  )}

                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Space>
    </Spin>
  );
};
